import { User } from '../types';
import { supabase } from './supabase';

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends AuthCredentials {
  full_name: string;
}

export interface AuthResponse {
  user: User | null;
  session: any | null;
  error: string | null;
}

export const authService = {
  async login({ email, password }: AuthCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, session: null, error: error.message };
      }

      // Create user object from auth data (users table not available yet)
      const user: User = {
        id: data.user?.id || '',
        email: data.user?.email || '',
        full_name: data.user?.user_metadata?.full_name || data.user?.email?.split('@')[0] || '',
        avatar_url: data.user?.user_metadata?.avatar_url || data.user?.user_metadata?.picture || undefined,
        created_at: data.user?.created_at || new Date().toISOString(),
        updated_at: data.user?.updated_at || new Date().toISOString()
      };

      // Skip updating last login (users table not available yet)

      return { user, session: data.session, error: null };
    } catch (error: any) {
      return { user: null, session: null, error: error.message };
    }
  },

  async register({ email, password, full_name }: RegisterData): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        return { user: null, session: null, error: error.message };
      }

      if (data.user) {
        // Skip creating user profile (users table not available yet)

        const user: User = {
          id: data.user.id,
          email: data.user.email || '',
          full_name: full_name,
          avatar_url: undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        return { user, session: data.session, error: null };
      }

      return { 
        user: null, 
        session: data.session, 
        error: 'Please check your email to confirm your registration.' 
      };
    } catch (error: any) {
      return { user: null, session: null, error: error.message };
    }
  },

  async logout(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) return { error: error.message };
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async getCurrentUser(): Promise<AuthResponse> {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        return { user: null, session: null, error: sessionError.message };
      }
      
      if (!sessionData.session) {
        return { user: null, session: null, error: null };
      }

      // Create user object from session data (users table not available yet)
      const user: User = {
        id: sessionData.session.user.id,
        email: sessionData.session.user.email || '',
        full_name: sessionData.session.user.user_metadata?.full_name || sessionData.session.user.email?.split('@')[0] || '',
        avatar_url: sessionData.session.user.user_metadata?.avatar_url || sessionData.session.user.user_metadata?.picture || undefined,
        created_at: sessionData.session.user.created_at || new Date().toISOString(),
        updated_at: sessionData.session.user.updated_at || new Date().toISOString()
      };

      return { user, session: sessionData.session, error: null };
    } catch (error: any) {
      return { user: null, session: null, error: error.message };
    }
  },

  async updateProfile(userId: string, userUpdateData: { full_name?: string; avatar_url?: string }): Promise<{ user: User | null; error: string | null }> {
    try {
      // Skip updating profile (users table not available yet)
      // Just return a basic user object for now
      const user: User = {
        id: userId,
        email: '',
        full_name: userUpdateData.full_name || '',
        avatar_url: userUpdateData.avatar_url || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      return { user, error: null };
    } catch (error: any) {
      return { user: null, error: error.message };
    }
  },

  async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) return { error: error.message };
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async updatePassword(password: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) return { error: error.message };
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  }
};